import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import useSWR, { mutate } from "swr";
import { AppConstants, PlatformApi } from "../../configs";
import { AxiosAuth } from "../../core";
import { ApiResponse, ApiSchemaCreateVendor, ApiSchemaCreateVendorCompany, ApiSchemaVendorData, WithPagination } from "../../models";
import { UtilsJQuery } from "../../utils/jQuery";
import { topLoaderProgress } from '../../configs/appConstants';

type Props = {
  parentCompanyId?: string,
  pageSize: number;
  pageNo: number;
  searchText?: string;
  setLoaderProgress: any;

};
let pagedVendorUrl: string;
export function useFunctionalityPagedVendors(props: Props) {
  const { pageSize, pageNo, searchText, setLoaderProgress, parentCompanyId } = props;
  // const [pagedVendorListCount, setPagedVendorListCount] = useState(0);

  pagedVendorUrl = PlatformApi.Vendors.PagedGetAll(parentCompanyId, pageSize, pageNo, searchText);

  const { data: apiPagedVendorList } = useSWR<{
    data: ApiSchemaVendorData[];
    count: number;
  }>(
    pagedVendorUrl,
    async () => {
      setLoaderProgress(topLoaderProgress.start);

      try {
        const response = await AxiosAuth.get<ApiResponse<WithPagination<ApiSchemaVendorData[]>>>(
          pagedVendorUrl
        );
        setLoaderProgress(topLoaderProgress.complete);
        return {
          data: response.data.data.data,
          count: response.data.data.count
        };
      } catch (e) {
        setLoaderProgress(topLoaderProgress.complete);
        toast.error("Failed to load vendor data.");
        console.log(e);
        throw e;
      };
    }
  );

  const pagedVendorList = useMemo(() => {
    return apiPagedVendorList?.data ?? [];
  }, [apiPagedVendorList]);

  const pagedVendorListCount = apiPagedVendorList?.count ?? 0;

  return {
    pagedVendorList,
    pagedVendorListCount,
    pagedVendorUrl
  }
}


type vendorProps = {
  parentCompanyId?: string;
  shouldVendorGetAllInvoke?: boolean;
  handleShow?: any;
}
export function useFunctionalityVendors(prop: vendorProps) {
  const { parentCompanyId, shouldVendorGetAllInvoke = false, handleShow } = prop;
  const vendorUrl = PlatformApi.Vendors.GetAll(parentCompanyId);

  const { data: apiVendorListData } = useSWR<ApiSchemaVendorData[]>(
    shouldVendorGetAllInvoke ? vendorUrl : null,
    () =>
      AxiosAuth.get<ApiResponse<WithPagination<ApiSchemaVendorData[]>>>(
        vendorUrl
      )
        .then((r) => r.data.data.data)
        .catch((e) => {
          console.error(e);
          return e;
        })
  );

  const vendorList = React.useMemo(() => {
    const data = Array.isArray(apiVendorListData) ? apiVendorListData : [];
    return data;
  }, [apiVendorListData]);


  const onAddVendor = React.useCallback(async (form: ApiSchemaCreateVendor) => {
    const btnLoading = UtilsJQuery.Ladda(".login-from-submit-btn");
    btnLoading.start?.();
    try {
      const response = await AxiosAuth.post(
        PlatformApi.Vendors.Create(),
        form
      );

      if (response.status === AppConstants.Api.StatusOK) {
        toast.success("Vendor Added Successfully");
        mutate(pagedVendorUrl)
        handleShow(false)
      }
      else {
        handleShow(true)
      }
    } catch (e) {
      toast.error("Failed to create vendor");
      console.log(e);
    }
    btnLoading.stop?.();

    return Promise.resolve(null);
  }, []);


  //no longer in use
  const onEditPhone = React.useCallback(async (form: { phoneNumber: string }, vendorId: any) => {
    const btnLoading = UtilsJQuery.Ladda(".editPhone-from-submit-btn");
    btnLoading.start?.();
    try {
      if (vendorId) {
        const res = await AxiosAuth.put(
          PlatformApi.Vendors.UpdatePhoneNumber(vendorId, form.phoneNumber)
        );

        if ((res.status = AppConstants.Api.StatusOK)) {
          // mutate(vendorUrl);
          // mutate(pagedVendorUrl);
          toast.success("Phone number edited");
        }
      } else {
        toast.error("no vendor id found");
      }
    } catch (e) {
      toast.error("Failed to update phone number");
    }

    btnLoading.stop?.();
    return Promise.resolve(null);
  }, []);

  const onEditVendorOverflowType = React.useCallback(async (form: { vendorOverflowType: string }, vendorId: any) => {
    const btnLoading = UtilsJQuery.Ladda(".editOverflowType-from-submit-btn");
    btnLoading.start?.();
    try {
      if (vendorId) {
        const res = await AxiosAuth.put(
          PlatformApi.Vendors.UpdateOverflowType(vendorId, form.vendorOverflowType)
        );

        if ((res.status = AppConstants.Api.StatusOK)) {
          // mutate(vendorUrl);
          // mutate(pagedVendorUrl);
          toast.success("Overflow type edited");
        }
      } else {
        toast.error("no vendor id found");
      }
    } catch (e) {
      toast.error("Failed to update overflow type");
    }

    btnLoading.stop?.();
    return Promise.resolve(null);
  }, []);

  const onEditVendorOverflowValue = React.useCallback(async (form: { vendorOverflowValue: string }, vendorId: any) => {
    const btnLoading = UtilsJQuery.Ladda(".editOverflowValue-from-submit-btn");
    btnLoading.start?.();
    try {
      if (vendorId) {
        const res = await AxiosAuth.put(
          PlatformApi.Vendors.UpdateOverflowValue(vendorId, form.vendorOverflowValue)
        );

        if ((res.status = AppConstants.Api.StatusOK)) {
          // mutate(vendorUrl);
          // mutate(pagedVendorUrl);
          toast.success("Overflow value edited");
        }
      } else {
        toast.error("no vendor id found");
      }
    } catch (e) {
      toast.error("Failed to update overflow value");
    }

    btnLoading.stop?.();
    return Promise.resolve(null);
  }, []);


  const onDeleteVendor = React.useCallback(async (dataToDelete: string) => {
    const btnLoading = UtilsJQuery.Ladda(".delete-button");
    btnLoading.start?.();
    try {
      const response = await AxiosAuth.delete(
        PlatformApi.Vendors.DeleteVendor(dataToDelete)
      );

      if (response.status === AppConstants.Api.StatusOK) {
        // mutate(vendorUrl);
        // mutate(pagedVendorUrl);
        toast.success("Vendor deleted successfully");
      }
    } catch (e) {
      toast.error("Failed to delete vendor.");
      return Promise.reject(e);
    }

    btnLoading.stop?.();
    return Promise.resolve(null);
  }, []);

  return {
    vendorList,
    onAddVendor,
    onEditPhone,
    onEditVendorOverflowValue,
    onEditVendorOverflowType,
    onDeleteVendor
  };
}
